<script lang="ts" setup>
import { ref, computed } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';


const props = defineProps(["answers", "translation"])
const answersExpanded = ref(false);

const toggleAnswers = () => {
    answersExpanded.value = !answersExpanded.value;
};


const visibleAnswers = computed(() => {
    if (answersExpanded.value || props.answers.value.length <= 3) {
        return props.answers.value;
    }
    return props.answers.value.slice(0, 3);
});



</script>


<template>
    <div>
        <div v-if="answers && answers.length > 0" class="answers-section mt-3">
            <div class="answers-header-container">
                <b>
                    <p class="mb-1 resultSectionHeader">
                        {{ translation?.staticTexts?.answersOverview || 'Your Answers' }}
                    </p>
                </b>
                <button v-if="answers.length > 3" @click="toggleAnswers" class="expand-button">
                    <FontAwesomeIcon :icon="answersExpanded ? 'chevron-up' : 'chevron-down'" />
                    {{ answersExpanded
                        ? (translation?.staticTexts?.showLess || 'Weniger anzeigen')
                        : `${translation?.staticTexts?.showAll || 'Alle anzeigen'} (${answers.length})`
                    }}
                </button>

            </div>

            <div class="answers-wrapper">
                <div class="answers-list">
                    <div v-for="(answer, index) in answers" :key="answer.questionIdentifier" class="answer-item"
                        :class="{ 'is-hidden': index >= 3 && !answersExpanded }">
                        <div class="answer-content">
                        <div class="question-content">
                            <p class="text-muted text-sm questionTitle">
                                {{ translation?.questions?.[answer.question._id]?.title }}
                            </p>
                            <span class="answer-number pr-1">{{ index + 1 }}.&nbsp;</span>
                            <strong class="qs-question-text">
                                {{ translation?.questions?.[answer.question._id]?.questionText }}
                            </strong>
                        </div>

                            <div class="selected-answer">
                                <span v-for="(option, i) in answer.selectedOptions" :key="option.id">
                                    {{ translation?.options?.[option.id]?.title }}<span
                                        v-if="i < answer.selectedOptions.length - 1">, </span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="answers.length > 3 && !answersExpanded" class="fade-overlay"></div>
            </div>

        </div>
        <div class="answers-info-box alert-box d-flex align-items-center mt-3" v-if="answers && answers.length > 0">
            <span class="alert-text d-flex align-items-center">
                <small class="mr-1">
                    <FontAwesomeIcon icon="info-circle" class="alert-icon" />
                </small>
                <small
                    class="answerAlert"
                    v-html="translation?.staticTexts?.answersDownloadDisclaimer || 'Your answers are not saved. To keep them, please download the PDF.'"></small>
            </span>
        </div>
    </div>
</template>

<style>

.answers-section {
    margin-top: 2rem;
    position: relative;
}

.answers-header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: nowrap;
}

.answers-header-container .resultSectionHeader {
    flex-shrink: 0;
    margin: 0 !important;
}

.expand-button {
    background: none;
    border: 1px solid #00dce6;
    color: #00dce6;
    padding: 0.25rem 0.75rem;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.2s ease;
    flex-shrink: 0;
    margin-left: 1rem;
}

.expand-button:hover {
    background-color: rgba(0, 220, 230, 0.1);
    transform: translateY(-1px);
}

.answers-wrapper {
    position: relative;
    overflow: hidden;
    margin-left: .5rem !important
}

.answers-list {
    display: flex;
    flex-direction: column;
}

.hidden-answer {
    max-height: 0;
    overflow: hidden;
    opacity: 0;
    transition: all 0.3s ease;
}

.answers-list.full {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    position: relative;
}

.answers-expanded .hidden-answer {
    max-height: 500px;
    opacity: 1;
}

.answers-list.collapsed {
    position: relative;
}

.answer-item {
    transition: all 0.4s ease;
    opacity: 1;
    max-height: 500px;
    overflow: hidden;
    padding: 0.75rem 0;
    border-bottom: 1px solid #f0f0f0;
}

.answer-item:last-child {
    border-bottom: none;
}

.answer-item.is-hidden {
    opacity: 0;
    max-height: 0;
    padding: 0;
    margin: 0;
    border: none;
    pointer-events: none;
}

.question-content {
    display: grid;
    grid-template-columns: 32px 1fr;
}

.answer-number {
    grid-column: 1;
    width: 32px;
}

.answer-content {
    flex: 1;
}

.qs-question-text {
    margin-bottom: 0.25rem;
    font-size: unset !important;
    grid-column: 2;
}

.questionTitle {
    grid-column: 2;       
    margin-bottom: 4px;
    color: #00dce6;
    margin-top: unset;     
}

.qs-question-text strong {
    font-weight: 600;
}

.selected-answer {
    color: #666;
    margin-left: 32px
}

.fade-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 60px;
    background: linear-gradient(to bottom, transparent, white);
    pointer-events: none;
}

.expand-enter-active,
.expand-leave-active {
    transition: max-height 0.3s ease;
    overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
    max-height: 0;
}

.expand-enter-to,
.expand-leave-from {
    max-height: 2000px;
}

.answers-info-box {
    margin-left: .5rem;
    margin-top: unset;
    margin-bottom: unset;
    padding: unset;
}

.answers-info-box p {
    margin-left: .2rem ;
    margin-top: unset ;
    margin-bottom: unset ;
    padding: unset ;
}

@media (max-width: 768px) {
    .answers-header-container {
        flex-wrap: wrap;
        gap: 0;
    }

    .expand-button {
        order: 3;
        width: 100%;
        margin-left: 0;
        margin-top: 0.5rem;
        margin-bottom: 1rem;
        padding: 0.4rem 0.75rem;
        font-size: 0.9rem;
        justify-content: center;
    }

    .answers-header-container .resultSectionHeader {
        order: 1;
        width: 100%;
    }

    .answers-header-container + .answers-wrapper {
        order: 2;
    }

    .answers-section {
        display: flex;
        flex-direction: column;
    }

    .answers-header-container {
        display: contents;
    }

    .answer-item {
        flex-direction: column;
        gap: 0.25rem;
    }

    .answer-number {
        min-width: auto;
    }
    .answers-info-box {
        margin-bottom: 1rem;;
    }
}

@media (max-width: 480px) {
    .expand-button {
        padding: 0.5rem 1rem;
        font-size: 0.95rem;
    }
}
</style>